import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';
import './FileUpload.css';

function FileUpload({ onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [broker, setBroker] = useState('');
  const [brokerError, setBrokerError] = useState('');
  const [tradeBookName, setTradeBookName] = useState(`TradeBook_${Date.now()}`);
  const [nameError, setNameError] = useState('');
  const [tradeBookCount, setTradeBookCount] = useState({ count: 0, maxCount: 5 });
  const [loadingCount, setLoadingCount] = useState(true);
  const brokerTooltipMap = {
    zerodha: 'Go to Console > Reports > Tradebook. Filter by date, hit View, then click Download to save your XLSX.',
    upstox: 'Navigate to My Account > Reports > Trade Report. Set your dates, click Get Report, and Download the XLSX'
  };
  const brokerTooltipText = brokerTooltipMap[broker] || '';

  useEffect(() => {
    const fetchTradeBookCount = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/trade-books/count`);
        setTradeBookCount(response.data);
      } catch (error) {
        console.error('Error fetching trade book count:', error);
      } finally {
        setLoadingCount(false);
      }
    };
    fetchTradeBookCount();
  }, []);

  const handleNameChange = (e) => {
    const value = e.target.value;
    // Only allow alphanumeric and underscore
    if (/^[a-zA-Z0-9_]*$/.test(value) && value.length <= 100) {
      setTradeBookName(value);
      setNameError('');
    } else if (value.length > 100) {
      setNameError('Trade book name must be 100 characters or less');
    } else {
      setNameError('Trade book name can only contain letters, numbers, and underscores');
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    // Validate broker selection
    if (!broker || broker === '') {
      setBrokerError('Please select a broker');
      return;
    }

    // Validate trade book name
    if (!tradeBookName || tradeBookName.trim() === '') {
      setNameError('Trade book name is required');
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(tradeBookName)) {
      setNameError('Trade book name can only contain letters, numbers, and underscores');
      return;
    }

    const file = acceptedFiles[0];
    setUploading(true);
    setMessage('');
    setError('');
    setNameError('');
    setBrokerError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('broker', broker);
    formData.append('tradeBookName', tradeBookName.trim());

    try {
      const response = await axios.post(`${API_BASE_URL}/api/trades/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage(response.data.message || `File uploaded successfully! Processed ${response.data.stats.completedTrades?.length || 0} completed trades.`);
      
      // Reset trade book name for next upload
      setTradeBookName(`TradeBook_${Date.now()}`);
      
      // Refresh trade book count
      try {
        const countResponse = await axios.get(`${API_BASE_URL}/api/trade-books/count`);
        setTradeBookCount(countResponse.data);
      } catch (countError) {
        console.error('Error fetching trade book count:', countError);
      }
      
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to upload file';
      setError(errorMessage);
      
      // If error is about max trade books, show suggestion
      if (errorMessage.includes('Maximum of 5 trade books')) {
        // The suggestion will be shown in the UI below
      }
      
      // Refresh trade book count
      try {
        const countResponse = await axios.get(`${API_BASE_URL}/api/trade-books/count`);
        setTradeBookCount(countResponse.data);
      } catch (countError) {
        console.error('Error fetching trade book count:', countError);
      }
    } finally {
      setUploading(false);
    }
  }, [onUploadSuccess, broker, tradeBookName]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    multiple: false
  });

  return (
    <div className="file-upload-container">
      <div className="card">
        <h2>Upload Trade File</h2>
        <p className="upload-description">
          Upload your trade book file (.xlsx, .xls, or .csv format). Select your broker to ensure proper parsing.
        </p>

        <div className="broker-selection">
          <div className="broker-selection-fields">
            <label htmlFor="broker-select">Select Broker:</label>
            <select
              id="broker-select"
              value={broker}
              onChange={(e) => {
                setBroker(e.target.value);
                setBrokerError('');
              }}
              className="broker-select"
              disabled={uploading}
              required
            >
              <option value="">Select Broker</option>
              <option value="zerodha">Zerodha</option>
              <option value="upstox">Upstox</option>
            </select>
            {broker && (
              <small className="broker-help-text" title={brokerTooltipText}>
                How to get trade book
              </small>
            )}
          </div>
          {brokerError && <div className="error-message" style={{ marginTop: '5px', fontSize: '13px' }}>{brokerError}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="trade-book-name">Trade Book Name:</label>
          <input
            id="trade-book-name"
            type="text"
            value={tradeBookName}
            onChange={handleNameChange}
            className="trade-book-name-input"
            placeholder="Enter trade book name"
            disabled={uploading}
            maxLength={100}
          />
          {nameError && <div className="error-message" style={{ marginTop: '5px', fontSize: '13px' }}>{nameError}</div>}
          <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
            Only letters, numbers, and underscores allowed. Max 100 characters.
          </small>
        </div>

        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
        >
          <input {...getInputProps()} disabled={uploading} />
          {uploading ? (
            <div className="upload-status">
              <div className="spinner"></div>
              <p>Uploading and processing file...</p>
            </div>
          ) : (
            <div className="dropzone-content">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <p className="dropzone-text">
                {isDragActive
                  ? 'Drop the file here...'
                  : 'Drag & drop a file here, or click to select'}
              </p>
              <p className="dropzone-hint">Supports .xlsx, .xls, and .csv files</p>
            </div>
          )}
        </div>

        {!loadingCount && tradeBookCount.count >= tradeBookCount.maxCount && (
          <div className="warning-message" style={{ 
            marginBottom: '20px', 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107',
            borderRadius: '4px',
            color: '#856404'
          }}>
            <strong>Maximum Trade Books Reached:</strong> You have reached the maximum limit of {tradeBookCount.maxCount} trade books. 
            To upload a new trade book, please delete an existing one from the <strong>Trade Books</strong> tab.
          </div>
        )}

        {message && <div className="success-message">{message}</div>}
        {error && (
          <div className="error-message">
            {error}
            {error.includes('Maximum of 5 trade books') && (
              <div style={{ marginTop: '10px', fontSize: '13px' }}>
                <strong>Suggestion:</strong> Please delete an existing trade book from the <strong>Trade Books</strong> tab to upload a new one.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FileUpload;
